import React, { useState } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip,
  Alert, FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Divider,
} from '@mui/material';
import { Schedule as ScheduleIcon, AccessTime as TimeIcon } from '@mui/icons-material';

const AvailabilityStep = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  isCompleted,
}) => {
  const [errors, setErrors] = useState({});

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
  ];

  const durationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
  ];

  // Initialize availability data
  const [availability, setAvailability] = useState(
    formData.weekly_availability || days.reduce((acc, day) => ({
      ...acc,
      [day.key]: { enabled: false, slots: [] }
    }), {})
  );

  const [appointmentDuration, setAppointmentDuration] = useState(
    formData.appointment_duration || 30
  );

  const handleDayToggle = (dayKey, enabled) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled,
        slots: enabled ? prev[dayKey].slots : []
      }
    }));
  };

  const addTimeSlot = (dayKey) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: [...prev[dayKey].slots, { start: '09:00', end: '10:00' }]
      }
    }));
  };

  const removeTimeSlot = (dayKey, slotIndex) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.filter((_, index) => index !== slotIndex)
      }
    }));
  };

  const updateTimeSlot = (dayKey, slotIndex, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: prev[dayKey].slots.map((slot, index) =>
          index === slotIndex ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Check if at least one day is enabled
    const enabledDays = Object.values(availability).filter((day) => day.enabled);
    if (enabledDays.length === 0) {
      newErrors.availability = 'Please enable at least one day for availability';
    }

    // Check if enabled days have time slots
    Object.entries(availability).forEach(([dayKey, day]) => {
      if (day.enabled && day.slots.length === 0) {
        newErrors[dayKey] = `Please add at least one time slot for ${days.find(d => d.key === dayKey)?.label}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const data = {
        weekly_availability: availability,
        appointment_duration: appointmentDuration,
      };
      onSubmit(data);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Weekly Availability
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Set your weekly schedule and appointment duration
      </Typography>

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Availability has been set successfully!
        </Alert>
      )}

      {/* Appointment Duration */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <TimeIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Appointment Duration
            </Typography>
          </Box>
          
          <FormControl fullWidth>
            <InputLabel>Default Appointment Duration</InputLabel>
            <Select
              value={appointmentDuration}
              onChange={(e) => setAppointmentDuration(e.target.value)}
              disabled={isCompleted}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Typography variant="body2" color="text.secondary" mt={1}>
            This will be the default duration for all appointments. You can customize individual slots if needed.
          </Typography>
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      <Grid container spacing={2}>
        {days.map((day) => {
          const dayData = availability[day.key];
          
          return (
            <Grid item xs={12} key={day.key}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                      <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        {day.label}
                      </Typography>
                    </Box>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={dayData.enabled}
                          onChange={(e) => handleDayToggle(day.key, e.target.checked)}
                          disabled={isCompleted}
                        />
                      }
                      label={dayData.enabled ? 'Available' : 'Not Available'}
                    />
                  </Box>

                  {dayData.enabled && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      
                      {/* Time Slots */}
                      <Typography variant="subtitle1" gutterBottom>
                        Time Slots
                      </Typography>
                      
                      {dayData.slots.map((slot: TimeSlot, slotIndex: number) => (
                        <Box key={slotIndex} display="flex" alignItems="center" gap={2} mb={2}>
                          <FormControl size="small">
                            <InputLabel>Start Time</InputLabel>
                            <Select
                              value={slot.start}
                              onChange={(e) => updateTimeSlot(day.key, slotIndex, 'start', e.target.value)}
                              disabled={isCompleted}
                            >
                              {timeSlots.map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Typography>to</Typography>
                          
                          <FormControl size="small">
                            <InputLabel>End Time</InputLabel>
                            <Select
                              value={slot.end}
                              onChange={(e) => updateTimeSlot(day.key, slotIndex, 'end', e.target.value)}
                              disabled={isCompleted}
                            >
                              {timeSlots.map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          {!isCompleted && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeTimeSlot(day.key, slotIndex)}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      ))}
                      
                      {!isCompleted && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => addTimeSlot(day.key)}
                          sx={{ mt: 1 }}
                        >
                          Add Time Slot
                        </Button>
                      )}
                      
                      {errors[day.key] && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {errors[day.key]}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {errors.availability && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.availability}
        </Alert>
      )}

      {/* Tips */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Tips for setting availability:</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>Set realistic hours that you can consistently maintain</li>
          <li>Consider buffer time between appointments</li>
          <li>You can always adjust your schedule later</li>
          <li>Patients will only see available time slots</li>
        </Typography>
      </Alert>

      {!isCompleted && (
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AvailabilityStep;
